import { useEffect, useMemo, useRef, useState } from 'react'

import type { OriginalColorSimulationImages } from '@ai-color-modules/gen_proto/api_proto/v1/ai_color_simulation_pb'
import type { MergedPolygonsByArea } from '@ai-color-modules/simulation/types'
import { filterVisibleSimulatedImages } from '@ai-color-modules/utils/imageFilters'

import type { ThumbnailItem } from './useThumbnailGroups'

type TabItem = {
  value: string
  label: string
  index: number
}

interface UseSimulationDetailStateOptions {
  images: OriginalColorSimulationImages[]
  tabs: TabItem[]
  /**
   * 最初に選択しておく履歴（生成画像）のID。共有リンクのアンカー（issue #2950）に使う。
   * 一覧に無ければ従来どおり（1枚目のカットの最新の履歴）。マウント時の images で決める
   */
  initialSelectedImageId?: bigint
}

/**
 * initialSelectedImageId を含むカットのタブ値と、そのカット内での履歴の位置を返す。見つからなければ null。
 */
const resolveInitialSelection = (
  images: OriginalColorSimulationImages[],
  tabs: TabItem[],
  imageId: bigint | undefined,
): { tabValue: string; processedIndex: number; processedCount: number } | null => {
  if (imageId === undefined) return null
  for (const tab of tabs) {
    const processed = filterVisibleSimulatedImages(images[tab.index]?.simulatedImage)
    const index = processed.findIndex(img => img.id === imageId)
    if (index >= 0) {
      return { tabValue: tab.value, processedIndex: index, processedCount: processed.length }
    }
  }
  return null
}

export const useSimulationDetailState = ({
  images,
  tabs,
  initialSelectedImageId,
}: UseSimulationDetailStateOptions) => {
  // 共有リンクのアンカー（最初に開く履歴）。state の初期値にだけ使う
  const [initialSelection] = useState(() =>
    resolveInitialSelection(images, tabs, initialSelectedImageId),
  )
  const [activeTab, setActiveTab] = useState<string>(
    initialSelection?.tabValue ?? tabs[0]?.value ?? 'no-image',
  )
  const [editingArea, setEditingArea] = useState<string | null>(null)
  const [openLayerId, setOpenLayerId] = useState<string | null>(null)
  const [areaColors, setAreaColors] = useState<Record<string, string>>({})
  // 屋根・外壁ベースエリアの素材選択. キーは areaColors と同じ「外壁」「屋根」.
  const [areaMaterials, setAreaMaterials] = useState<Record<string, bigint>>({})
  // カスタムエリアからベースエリアに統合（結合）されたポリゴン領域（issue #2224）.
  // キーは areaColors と同じ日本語ベースエリアラベル（「外壁」等）.
  const [mergedPolygonsByArea, setMergedPolygonsByArea] = useState<MergedPolygonsByArea>({})
  // ベースエリアごとの「色 or 素材どちらが現在 active か」.
  // 同一エリアでの色/素材の同時実行は仕様上発生しないが、state は両方独立に保持し、
  // 最後に選んだ方を active として扱う（タブ切替で色↔素材を試行錯誤できるようにするため）.
  const [areaSelectionMode, setAreaSelectionMode] = useState<Record<string, 'color' | 'material'>>(
    {},
  )
  // 「ユーザーが Select で明示選択した分だけ」を保持するエリア別プリセット選択
  // （企業のみ切替可能・永続化しない UI 一時状態）.
  // キーは editingArea の値体系（ベースエリアは日本語名「外壁」等、カスタムは 'subarea-<idx>'）.
  // 値は preset ULID または 'all'（すべての色 = プリセット非適用）.
  //
  // デフォルト（baseAreaDefaultPresetIds）をここへコピーしないこと。実際に適用される
  // 「実効プリセット」は resolveEffectivePresetId で毎回導出する（未選択エリアは
  // デフォルトへ追従し、削除済み ID は 'all' に正規化される）.
  const [userSelectedPresetByArea, setUserSelectedPresetByArea] = useState<
    Record<string, string | 'all'>
  >({})
  const [isSubAreaModeActive, setIsSubAreaModeActive] = useState(false)
  const [excludeEnabled, setExcludeEnabled] = useState(false)
  const [isVerticalLayout, setIsVerticalLayout] = useState(false)
  const [isOriginalHidden, setIsOriginalHidden] = useState(false)
  const [displayMode, setDisplayMode] = useState<'preview' | 'edit'>('edit')
  const [unifyColor, setUnifyColor] = useState(false)
  const [isCustomAreaSettingOpen, setIsCustomAreaSettingOpen] = useState(false)
  const [editingCustomAreaIndex, setEditingCustomAreaIndex] = useState<number | null>(null)
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | undefined>(undefined)
  const [isShareLinkLoading, setIsShareLinkLoading] = useState(false)
  const [shareError, setShareError] = useState<string | undefined>(undefined)
  const [maskAdjustError, setMaskAdjustError] = useState<string | undefined>(undefined)
  const [selectedProcessedIndexByTab, setSelectedProcessedIndexByTab] = useState<
    Record<string, number>
  >(() =>
    initialSelection ? { [initialSelection.tabValue]: initialSelection.processedIndex } : {},
  )
  const [originalDimensions, setOriginalDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  const isEditMode = displayMode === 'edit'
  const isPreviewMode = displayMode === 'preview'
  const currentTabImages = useMemo(
    () => images.find(image => image.id?.toString() === activeTab),
    [images, activeTab],
  )
  const hasProcessedImagesForTab = useMemo(
    () => Boolean(filterVisibleSimulatedImages(currentTabImages?.simulatedImage).length > 0),
    [currentTabImages],
  )
  const isOriginalSelected = selectedProcessedIndexByTab[activeTab] === -1
  const showOriginalImage = isPreviewMode
    ? !isOriginalHidden
    : !hasProcessedImagesForTab || isOriginalSelected

  useEffect(() => {
    if (displayMode === 'preview') {
      setEditingArea(null)
      setOpenLayerId(null)
      setIsCustomAreaSettingOpen(false)
      setEditingCustomAreaIndex(null)
    }
  }, [displayMode])

  // アンカーで開いたカットは「履歴が増えた」扱いにしない（増えた扱いだと最新の履歴へ選択が飛ぶ）
  const prevProcessedCountRef = useRef<Record<string, number>>(
    initialSelection ? { [initialSelection.tabValue]: initialSelection.processedCount } : {},
  )

  useEffect(() => {
    const processedImages = filterVisibleSimulatedImages(currentTabImages?.simulatedImage)
    const currentCount = processedImages.length
    const prevCount = prevProcessedCountRef.current[activeTab] ?? 0

    if (currentCount > prevCount && currentCount > 0) {
      const latestIndex = currentCount - 1
      setSelectedProcessedIndexByTab(prev => ({
        ...prev,
        [activeTab]: latestIndex,
      }))
    }

    prevProcessedCountRef.current = {
      ...prevProcessedCountRef.current,
      [activeTab]: currentCount,
    }
  }, [activeTab, currentTabImages])

  const prevTabCountRef = useRef(tabs.length)

  useEffect(() => {
    if (tabs.length === 0) {
      setActiveTab('no-image')
      prevTabCountRef.current = 0
      return
    }

    if (tabs.length > prevTabCountRef.current && prevTabCountRef.current > 0) {
      const lastTab = tabs[tabs.length - 1]
      setActiveTab(lastTab.value)
      setSelectedProcessedIndexByTab(prev => ({
        ...prev,
        [lastTab.value]: -1,
      }))
      prevProcessedCountRef.current = {
        ...prevProcessedCountRef.current,
        [lastTab.value]: filterVisibleSimulatedImages(images[lastTab.index]?.simulatedImage).length,
      }
    } else {
      const current = tabs.find(tab => tab.value === activeTab)
      if (!current) {
        setActiveTab(tabs[0].value)
      }
    }

    prevTabCountRef.current = tabs.length
  }, [activeTab, images, tabs])

  useEffect(() => {
    setSelectedProcessedIndexByTab(prev => {
      let hasChanged = false
      const next = { ...prev }

      Object.entries(next).forEach(([tabValue, storedSelection]) => {
        const tab = tabs.find(item => item.value === tabValue)
        if (!tab) {
          delete next[tabValue]
          hasChanged = true
          return
        }

        const image = images[tab.index]
        const processedImages = filterVisibleSimulatedImages(image?.simulatedImage)

        if (processedImages.length === 0) {
          delete next[tabValue]
          hasChanged = true
          return
        }

        if (storedSelection === -1) {
          return
        }

        if (storedSelection < 0) {
          next[tabValue] = 0
          hasChanged = true
          return
        }

        const maxIndex = processedImages.length - 1
        if (storedSelection > maxIndex) {
          next[tabValue] = maxIndex
          hasChanged = true
        }
      })

      return hasChanged ? next : prev
    })
  }, [images, tabs])

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    setOriginalDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight,
    })
  }

  const handleOpenColorPalette = (area: string) => {
    setEditingArea(area)
  }

  const handleDismissColorPalette = () => {
    setOpenLayerId(null)
    setEditingArea(null)
  }

  /** パレットパネルだけ閉じ、エリアのフォーカスは維持する */
  const handleCloseColorPalettePanel = () => {
    setEditingArea(null)
  }

  const handleBaseAreaReset = (area: string) => {
    setAreaColors(prev => {
      if (!(area in prev)) return prev
      const next = { ...prev }
      delete next[area]
      return next
    })
    setAreaMaterials(prev => {
      if (!(area in prev)) return prev
      const next = { ...prev }
      delete next[area]
      return next
    })
    setAreaSelectionMode(prev => {
      if (!(area in prev)) return prev
      const next = { ...prev }
      delete next[area]
      return next
    })
    // 統合ポリゴン（issue #2224）もリセット対象に含める。
    setMergedPolygonsByArea(prev => {
      if (!(area in prev)) return prev
      const next = { ...prev }
      delete next[area]
      return next
    })
  }

  const handleThumbnailSelect = (thumb: ThumbnailItem) => {
    if (!thumb.tabValue) {
      return
    }

    const tabValue = thumb.tabValue
    setActiveTab(tabValue)

    if (thumb.type === 'processed' && typeof thumb.processedIndex === 'number') {
      const processedIndex = thumb.processedIndex
      setSelectedProcessedIndexByTab(prev => {
        if (prev[tabValue] === processedIndex) {
          return prev
        }
        return {
          ...prev,
          [tabValue]: processedIndex,
        }
      })
      return
    }

    if (thumb.type === 'original') {
      setSelectedProcessedIndexByTab(prev => {
        if (prev[tabValue] === -1) {
          return prev
        }
        return {
          ...prev,
          [tabValue]: -1,
        }
      })
    }
  }

  return {
    activeTab,
    setActiveTab,
    editingArea,
    setEditingArea,
    openLayerId,
    setOpenLayerId,
    areaColors,
    setAreaColors,
    areaMaterials,
    setAreaMaterials,
    mergedPolygonsByArea,
    setMergedPolygonsByArea,
    areaSelectionMode,
    setAreaSelectionMode,
    userSelectedPresetByArea,
    setUserSelectedPresetByArea,
    isSubAreaModeActive,
    setIsSubAreaModeActive,
    excludeEnabled,
    setExcludeEnabled,
    isVerticalLayout,
    setIsVerticalLayout,
    isOriginalHidden,
    setIsOriginalHidden,
    displayMode,
    setDisplayMode,
    isEditMode,
    isPreviewMode,
    unifyColor,
    setUnifyColor,
    isCustomAreaSettingOpen,
    setIsCustomAreaSettingOpen,
    editingCustomAreaIndex,
    setEditingCustomAreaIndex,
    isShareDialogOpen,
    setIsShareDialogOpen,
    shareUrl,
    setShareUrl,
    isShareLinkLoading,
    setIsShareLinkLoading,
    shareError,
    setShareError,
    maskAdjustError,
    setMaskAdjustError,
    selectedProcessedIndexByTab,
    setSelectedProcessedIndexByTab,
    originalDimensions,
    setOriginalDimensions,
    currentTabImages,
    hasProcessedImagesForTab,
    isOriginalSelected,
    showOriginalImage,
    handleImageLoad,
    handleOpenColorPalette,
    handleDismissColorPalette,
    handleCloseColorPalettePanel,
    handleBaseAreaReset,
    handleThumbnailSelect,
  }
}
